import { gutterLineClass, GutterMarker, EditorView } from '@codemirror/view';
import {
  StateField,
  StateEffect,
  RangeSet,
  RangeSetBuilder,
  type EditorState,
  type Extension,
} from '@codemirror/state';
import { getChunks } from '@codemirror/merge';

/** Marker that tags a gutter line (incl. the line number) as changed. */
class ChangedLineMarker extends GutterMarker {
  elementClass = 'cm-changedLineNumber';
}
const changedMarker = new ChangedLineMarker();

/** Fired to force the field to recompute once the merge chunks are ready. */
const refreshChanged = StateEffect.define<null>();

function computeChangedGutter(state: EditorState): RangeSet<GutterMarker> {
  const info = getChunks(state);
  const builder = new RangeSetBuilder<GutterMarker>();
  if (!info || !info.side) return builder.finish();

  const doc = state.doc;
  for (const chunk of info.chunks) {
    const from = info.side === 'a' ? chunk.fromA : chunk.fromB;
    const to = info.side === 'a' ? chunk.toA : chunk.toB;
    // An empty range on this side means the change was purely on the other
    // side (a pure insertion/deletion); there is no line here to highlight.
    if (to <= from) continue;
    const startLine = doc.lineAt(from).number;
    const endLine = doc.lineAt(Math.min(to - 1, doc.length)).number;
    for (let n = startLine; n <= endLine; n++) {
      const lineFrom = doc.line(n).from;
      builder.add(lineFrom, lineFrom, changedMarker);
    }
  }
  return builder.finish();
}

/**
 * A gutter extension that adds `cm-changedLineNumber` to every line involved in
 * a merge chunk, so the line-number itself is visibly highlighted (not just the
 * line body). Recomputes on every transaction because editing one side of a
 * MergeView changes the other side's chunks.
 */
export function changedLineGutter(): Extension {
  const field = StateField.define<RangeSet<GutterMarker>>({
    create: (state) => computeChangedGutter(state),
    update: (_value, tr) => computeChangedGutter(tr.state),
    provide: (f) => gutterLineClass.from(f),
  });
  return field;
}

/**
 * Force both sides of a freshly-built MergeView to recompute their changed-line
 * gutters. Needed because chunk state may not be available during the initial
 * StateField.create, before the merge extensions finish installing.
 */
export function refreshChangedGutters(...views: EditorView[]): void {
  for (const view of views) {
    view.dispatch({ effects: refreshChanged.of(null) });
  }
}
